import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users,
  Search,
  TrendingUp,
  Shield,
  ExternalLink,
  Filter,
  ArrowLeft,
  CheckCircle
} from 'lucide-react';

export default function RecruiterDashboard() {
  const { token } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minScore, setMinScore] = useState('');
  const [kycFilter, setKycFilter] = useState('');

  useEffect(() => {
    fetchProfiles();
  }, []);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      let url = `${API}/recruiter/profiles?limit=50`;
      if (minScore) url += `&min_score=${minScore}`;
      if (kycFilter) url += `&kyc_status=${kycFilter}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfiles(response.data);
    } catch (error) {
      toast.error('Failed to fetch profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchProfiles();
  };

  const handleViewProfile = (shareToken) => {
    window.open(`/passport/${shareToken}`, '_blank');
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" data-testid="recruiter-dashboard">
      <div className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6"
          data-testid="back-to-dashboard-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Recruiter Dashboard
              </h1>
              <p className="text-gray-600">Browse verified candidate profiles</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6 border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Search Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Minimum TRUST-AI Score</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="e.g., 70"
                  value={minScore}
                  onChange={(e) => setMinScore(e.target.value)}
                  data-testid="min-score-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">KYC Status</label>
                <select
                  value={kycFilter}
                  onChange={(e) => setKycFilter(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg"
                  data-testid="kyc-filter-select"
                >
                  <option value="">All</option>
                  <option value="verified">Verified</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={handleSearch}
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  data-testid="search-button"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {loading ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading profiles...</p>
          </div>
        ) : profiles.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No profiles found matching your criteria</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              Found {profiles.length} verified profiles
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {profiles.map((profile) => (
                <Card
                  key={profile.id}
                  className="card-hover border-0 shadow-lg"
                  data-testid={`profile-card-${profile.id}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold">
                          {profile.full_name.charAt(0)}
                        </div>
                        <div>
                          <CardTitle className="text-lg">{profile.full_name}</CardTitle>
                          <Badge className="mt-1 bg-green-100 text-green-700 text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {profile.kyc_status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    {/* TRUST-AI Score */}
                    <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg text-center">
                      <div className="flex items-center justify-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-indigo-600" />
                        <span className="text-xs font-medium text-indigo-900">TRUST-AI Score</span>
                      </div>
                      <div className={`text-4xl font-bold ${getScoreColor(profile.trust_score)}`}>
                        {profile.trust_score?.toFixed(1) || 'N/A'}
                      </div>
                      <p className="text-xs text-gray-500">out of 100</p>
                    </div>

                    {/* Email */}
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">Email:</span> {profile.email}
                    </div>

                    {/* View Profile Button */}
                    <Button
                      onClick={() => handleViewProfile(profile.share_token)}
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                      data-testid={`view-profile-button-${profile.id}`}
                    >
                      View Full Profile
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}